import Container from '@brands/Components/Container/Container';
import PhiDisclaimerFooter from '@brands/Components/PhiDisclaimerFooter/PhiDisclaimerFooter';
import LinearProgressDisplay from '@brands/Components/ProgressDisplay/ProgressDisplay';
import BrandLogo from '@brands/Components/BrandLogo/BrandLogo';
import closeIcon from '@brands/Components/PatientIntakeNavigation/Icons/patientIntakeNavigationClose.svg';
import intakeLogo from '@brands/Components/PatientIntakeNavigation/Icons/patientIntakeNavigationLogo.svg';
import AbandonIntakeProcessModal from '@brands/Components/PatientIntakeNavigation/Modal/AbandonIntakeProcessModal';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectClientBranding } from '@brands/store/selectors/clientBranding';
import { setSelectedPatient } from '@brands/store/slices/patientFormSlice';
import { setSelectedPatientByAdmin } from '@brands/store/slices/selectedPatientSlice';
import { TablePaginationKey } from '@brands/store/slices/tablePaginationSlice';
import InfoIcon from '@mui/icons-material/Info';
import { MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';

import { Loading } from '../../../../Components/LoadingSpinner/Loading';
import MinimalDateRangePicker from '../../../../Components/MinimalDateRangePicker/MinimalDateRangePicker';
import useDebounce from '../../../../hooks/useDebounce';
import { usePersistedRowsPerPage } from '../../../../hooks/usePersistedRowsPerPage';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useReduxHook';
import usePatientSelectUsers, { PatientSelectListMode } from '../../../../hooks/usePatientSelectUsers';
import { IListCollectorUsersParams } from '../../../../services/identity/listCollectorUsers';
import { IListUsersParams } from '../../../../services/identity/listAllUsers';
import { listAllClientTags } from '../../../../services/identity/listAllClientTags';
import { getClientDivisionOptions } from '../../../../services/identity/clientDivisionOptions';
import { getCollectorUserById } from '../../../../services/identity/getCollectorUserById';
import { ClientTag, UserProfile, UserRoleName } from '../../../../services/identity/types/UserProfile';
import { getStaffReviewProfileBasePath } from '@brands/Utils/reviewProfileBasePath';
import { displayErrorDetails } from '../../../../Utils/displayError';
import { formatDateOfBirth } from '../../../../Utils/formatDate';
import CADataGrid, { caGridStyles, ColumnDef } from '../../CareAdvisor/Components/CADataGrid';
import adminStyles from '../../adminDashboard.module.scss';
import styles from './adminPatientSelect.module.scss';

export type AdminPatientSelectMode = 'admin' | 'collector';

const TYPE_OPTIONS = [
  { value: UserRoleName.Patient, label: 'Patient' },
  { value: UserRoleName.Dependent, label: 'Dependent' },
];

function dateToYmd(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

type ClientTagOption = {
  value: string;
  label: string;
};

const AdminPatientSelect: React.FC<{ mode?: AdminPatientSelectMode }> = ({ mode = 'admin' }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const roleNavigate = useRoleNavigate();
  const { userInfo } = useAppSelector(selectAuth);
  const clientBranding = useAppSelector(selectClientBranding);
  const [displayAbandonIntakeProcessModal, setDisplayAbandonIntakeProcessModal] = useState(false);

  const preselectProfileUserId = useMemo((): number | null => {
    const s = location.state as { preselectProfileUserId?: unknown } | null | undefined;
    const v = s?.preselectProfileUserId;
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [location.state]);

  const dataMode: PatientSelectListMode =
    mode === 'collector' && userInfo.role.name === UserRoleName.Collector
      ? 'collector'
      : userInfo.role.name === UserRoleName.CareAdvisor
        ? 'careAdvisor'
        : 'admin';

  const shouldProfilePreselect = dataMode === 'collector' && preselectProfileUserId != null;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = usePersistedRowsPerPage(
    TablePaginationKey.SuperAdminPatientSelect,
    10,
  );

  const [filterName, setFilterName] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterClientTag, setFilterClientTag] = useState('');
  const [filterClientDivision, setFilterClientDivision] = useState('');
  const [clientTagOptions, setClientTagOptions] = useState<ClientTagOption[]>([]);
  const [allClientTags, setAllClientTags] = useState<ClientTag[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedDobRange, setSelectedDobRange] = useState<[Date, Date] | null>(null);

  const debouncedName = useDebounce(filterName, 500);
  const debouncedEmail = useDebounce(filterEmail, 500);

  const handleDobDateRangeChange = useCallback((_key: string, value: [Date, Date] | null) => {
    setSelectedDobRange(value);
  }, []);

  useEffect(() => {
    const fetchClientTags = async (): Promise<void> => {
      try {
        const { nodes } = await listAllClientTags(userInfo.role.name);
        const withCode = nodes.filter((t) => Boolean(t.code));
        setClientTagOptions(withCode.map((t) => ({ value: t.code as string, label: t.name })));
        setAllClientTags(withCode);
      } catch (error) {
        displayErrorDetails(error);
      }
    };
    fetchClientTags();
  }, []);

  const clientTagLookup = useMemo(
    () =>
      clientTagOptions.reduce<Record<string, string>>((acc, opt) => {
        acc[opt.value] = opt.label;
        return acc;
      }, {}),
    [clientTagOptions],
  );

  const selectedClientTagIdsForDivision = useMemo(
    () => allClientTags.filter((t) => t.code === filterClientTag).map((t) => t.id),
    [allClientTags, filterClientTag],
  );

  const divisionFilterOptions = useMemo(
    () => getClientDivisionOptions(allClientTags, selectedClientTagIdsForDivision),
    [allClientTags, selectedClientTagIdsForDivision],
  );

  const adminParams = useMemo((): Pick<
    IListUsersParams,
    | 'sort_by' | 'sort_direction' | 'type' | 'limit' | 'skip' | 'page'
    | 'full_name' | 'email' | 'client_tags' | 'client_division_ids' | 'date_of_birth_start' | 'date_of_birth_end' | 'status'
  > => {
    const base: Pick<
      IListUsersParams,
      | 'sort_by' | 'sort_direction' | 'type' | 'limit' | 'skip' | 'page'
      | 'full_name' | 'email' | 'client_tags' | 'client_division_ids' | 'date_of_birth_start' | 'date_of_birth_end' | 'status'
    > = {
      sort_by: 'full_name',
      sort_direction: 'asc',
      type: filterType ? [filterType as UserRoleName] : [UserRoleName.Patient, UserRoleName.Dependent],
      status: ['Active', 'Invited', 'Not Invited'],
      limit: rowsPerPage,
      skip: page * rowsPerPage,
      page: page + 1,
    };
    if (debouncedName) base.full_name = debouncedName;
    if (debouncedEmail) base.email = debouncedEmail;
    if (filterClientTag) base.client_tags = [filterClientTag];
    if (filterClientDivision) base.client_division_ids = [Number(filterClientDivision)];
    if (selectedDobRange?.[0] && selectedDobRange?.[1]) {
      base.date_of_birth_start = dateToYmd(selectedDobRange[0]);
      base.date_of_birth_end = dateToYmd(selectedDobRange[1]);
    }
    return base;
  }, [
    page,
    rowsPerPage,
    debouncedName,
    debouncedEmail,
    filterType,
    filterClientTag,
    filterClientDivision,
    selectedDobRange,
  ]);

  const collectorParams = useMemo((): IListCollectorUsersParams => {
    const base: IListCollectorUsersParams = {
      sort_by: 'full_name',
      sort_direction: 'asc',
      type: filterType ? [filterType as UserRoleName] : [UserRoleName.Patient, UserRoleName.Dependent],
      status: ['Active', 'Invited', 'Not Invited'],
      limit: rowsPerPage,
      skip: page * rowsPerPage,
    };
    if (debouncedName) base.full_name = debouncedName;
    if (debouncedEmail) base.email = debouncedEmail;
    if (filterClientTag) base.client_tags = [filterClientTag];
    if (filterClientDivision) base.client_division_ids = [Number(filterClientDivision)];
    if (selectedDobRange?.[0] && selectedDobRange?.[1]) {
      base.date_of_birth_start = dateToYmd(selectedDobRange[0]);
      base.date_of_birth_end = dateToYmd(selectedDobRange[1]);
    }
    return base;
  }, [
    page,
    rowsPerPage,
    debouncedName,
    debouncedEmail,
    filterType,
    filterClientTag,
    filterClientDivision,
    selectedDobRange,
  ]);

  const debouncedAdminParams = useDebounce(adminParams, 300);
  const debouncedCollectorParams = useDebounce(collectorParams, 300);
  const { users, totalRecords, dataLoading, loadError, retry } = usePatientSelectUsers(
    dataMode,
    debouncedAdminParams,
    debouncedCollectorParams,
  );

  const getClientTagNames = useCallback(
    (user: UserProfile): string => {
      if (!user.clients || user.clients.length === 0) return '';
      return user.clients
        .map((c) => clientTagLookup[c.name] || c.name)
        .filter(Boolean)
        .join(', ');
    },
    [clientTagLookup],
  );

  const handleRowClick = useCallback((user: UserProfile) => {
    setSelectedUserId((prev) => (prev === user.id ? null : user.id));
  }, []);

  const applyPatientSelectionAndContinue = useCallback(
    (selectedUser: UserProfile, navigateOpts?: { replace?: boolean }) => {
      dispatch(setSelectedPatient({ value: selectedUser.id.toString(), label: selectedUser.full_name }));
      dispatch(setSelectedPatientByAdmin({ selectedPatient: selectedUser }));
      const base = getStaffReviewProfileBasePath(userInfo.role.name);
      navigate(`${base}/${selectedUser.role.name}/${selectedUser.id}/create-consultation-for`, navigateOpts);
    },
    [dispatch, navigate, userInfo.role.name],
  );

  const handleNext = useCallback(() => {
    if (selectedUserId == null) return;
    const selectedUser = users.find((u) => u.id === selectedUserId);
    if (!selectedUser) return;
    applyPatientSelectionAndContinue(selectedUser);
  }, [selectedUserId, users, applyPatientSelectionAndContinue]);

  useEffect(() => {
    if (!shouldProfilePreselect || preselectProfileUserId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const selectedUser = await getCollectorUserById(preselectProfileUserId);
        if (cancelled) return;
        applyPatientSelectionAndContinue(selectedUser, { replace: true });
      } catch (error: unknown) {
        if (cancelled) return;
        displayErrorDetails(error);
        navigate(location.pathname, { replace: true, state: {} });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shouldProfilePreselect, preselectProfileUserId, applyPatientSelectionAndContinue, navigate, location.pathname]);

  const handleCollectorExit = useCallback(() => {
    roleNavigate('/dashboard');
  }, [roleNavigate]);

  const patientNameHeader = mode === 'collector' ? 'Patient name' : 'Patient Name';
  const patientTypeHeader = mode === 'collector' ? 'Patient type' : 'Patient Type';
  const clientTagHeader = mode === 'collector' ? 'Client tag' : 'Client Tag';

  const columns: ColumnDef<UserProfile>[] = useMemo(
    () => [
      {
        key: 'full_name',
        label: patientNameHeader,
        widthPx: 200,
        renderCell: (row) => <span className={caGridStyles.cellText}>{row.full_name}</span>,
        filterContent: (
          <TextField
            size="small"
            placeholder={patientNameHeader}
            value={filterName}
            onChange={(e) => { setFilterName(e.target.value); setPage(0); }}
            fullWidth
          />
        ),
      },
      {
        key: 'date_of_birth',
        label: 'DOB',
        widthPx: 160,
        renderCell: (row) => {
          const dob = row.date_of_birth ?? row.basic_info?.date_of_birth;
          return (
            <span className={caGridStyles.cellText}>
              {formatDateOfBirth(dob)}
            </span>
          );
        },
        filterContent: (
          <MinimalDateRangePicker
            handleDateRangeFilterChange={handleDobDateRangeChange}
            filterKey="date_of_birth"
            selectedRange={selectedDobRange}
          />
        ),
      },
      {
        key: 'email',
        label: 'Email',
        widthPx: 240,
        renderCell: (row) => <span className={caGridStyles.cellText}>{row.email}</span>,
        filterContent: (
          <TextField
            size="small"
            placeholder="Email"
            value={filterEmail}
            onChange={(e) => { setFilterEmail(e.target.value); setPage(0); }}
            fullWidth
          />
        ),
      },
      {
        key: 'type',
        label: patientTypeHeader,
        widthPx: 140,
        renderCell: (row) => <span className={caGridStyles.cellText}>{row.role.name}</span>,
        filterContent: (
          <Select
            size="small"
            value={filterType}
            onChange={(e: SelectChangeEvent) => { setFilterType(e.target.value); setPage(0); }}
            displayEmpty
            fullWidth
          >
            <MenuItem value=""><em>Patient Type</em></MenuItem>
            {TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: 'client_tags',
        label: clientTagHeader,
        widthPx: 180,
        renderCell: (row) => <span className={caGridStyles.cellText}>{getClientTagNames(row)}</span>,
        filterContent: (
          <Select
            size="small"
            value={filterClientTag}
            onChange={(e: SelectChangeEvent) => {
              setFilterClientTag(e.target.value);
              setFilterClientDivision('');
              setPage(0);
            }}
            displayEmpty
            fullWidth
          >
            <MenuItem value=""><em>Client Tag</em></MenuItem>
            {clientTagOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        ),
      },
      {
        key: 'client_divisions',
        label: 'Division(s)',
        widthPx: 160,
        renderCell: (row) => (
          <span className={caGridStyles.cellText}>{(row.divisions || []).map((d) => d.name).join(', ')}</span>
        ),
        filterContent: (
          <Select
            size="small"
            value={filterClientDivision}
            disabled={!filterClientTag}
            onChange={(e: SelectChangeEvent) => { setFilterClientDivision(e.target.value); setPage(0); }}
            displayEmpty
            fullWidth
          >
            <MenuItem value="">
              <em>{filterClientTag ? 'Division' : 'Select a client tag first'}</em>
            </MenuItem>
            {divisionFilterOptions.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
            ))}
          </Select>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filterName,
      filterEmail,
      filterType,
      filterClientTag,
      filterClientDivision,
      divisionFilterOptions,
      clientTagOptions,
      selectedDobRange,
      handleDobDateRangeChange,
      getClientTagNames,
      mode,
    ],
  );

  const inner = (
    <div className={styles.container}>
      {mode === 'collector' ? (
        <>
          <div className={styles.bannerCollector}>
            <InfoIcon className={styles.bannerCollectorInfoIcon} aria-hidden="true" />
            <div className={styles.bannerCollectorBody}>
              <div className={styles.bannerCollectorHeadline}>Creating a Consultation</div>
              <div className={styles.bannerCollectorDetail}>You are currently creating a new case</div>
            </div>
          </div>
          <h1 className={styles.titleCollector}>
            Who is the appointment for?{' '}
            <span className={styles.titleRequired} title="Required">*</span>
          </h1>
        </>
      ) : (
        <>
          <div className={styles.banner}>
            <span className={styles.bannerTitle}>Creating a Consultation</span>
            <span>- You are currently creating a new case</span>
          </div>
          <h1 className={styles.title}>
            Who is the appointment for?{' '}
            <span className={styles.titleRequired} title="Required">*</span>
          </h1>
        </>
      )}

      <div className={styles.gridContainer}>
        {loadError && <div role="alert">{loadError} <button type="button" onClick={retry}>Retry</button></div>}
        <CADataGrid
          columns={columns}
          rows={users}
          getRowId={(row) => row.id}
          selectedRowId={selectedUserId}
          onRowClick={handleRowClick}
          count={totalRecords}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(p) => setPage(p)}
          onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setPage(0); }}
          loading={dataLoading}
          showManageColumns={false}
        />
      </div>

      <div className={mode === 'collector' ? styles.footerCollector : styles.footer}>
        {mode === 'collector' && (
          <button type="button" className={styles.backBtn} onClick={handleCollectorExit}>
            <MdKeyboardArrowLeft className={styles.backBtnIcon} aria-hidden />
            Back
          </button>
        )}
        <button type="button" className={styles.nextBtn} disabled={dataLoading || Boolean(loadError) || !users.some(user => user.id === selectedUserId)} onClick={handleNext}>
          Next
        </button>
      </div>
    </div>
  );

  if (mode === 'collector') {
    return (
      <div className={styles.collectorFullPage}>
        <header className={styles.patientSelectIntakeHeader}>
          <div className={styles.patientSelectIntakeHeaderTop}>
            <BrandLogo
              customLogoUrl={clientBranding?.logo_url}
              fallbackLogo={intakeLogo}
              className={styles.patientSelectIntakeLogo}
            />
            <button
              type="button"
              onClick={() => setDisplayAbandonIntakeProcessModal(true)}
              className={styles.patientSelectIntakeCloseIcon}
              aria-label="Close and exit"
            >
              <img src={closeIcon} alt="" />
            </button>
          </div>
          <LinearProgressDisplay progress={20} />
        </header>
        <main className={styles.collectorMain}>
          {shouldProfilePreselect ? (
            <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', width: '100%' }}>
              <Loading dotted isHeightAuto />
            </div>
          ) : (
            <div className={styles.containerCollector}>{inner}</div>
          )}
          <PhiDisclaimerFooter />
        </main>
        {displayAbandonIntakeProcessModal && (
          <AbandonIntakeProcessModal
            setDisplayAbandonIntakeProcessModal={setDisplayAbandonIntakeProcessModal}
            onConfirmExit={handleCollectorExit}
          />
        )}
      </div>
    );
  }

  return (
    <Container
      patientDashboard
      isVisibleOnMobile={false}
      childClassName={adminStyles.adminDashboardShell}
      containerStyle={{ overflowY: 'hidden' }}
      childrenStyle={{ alignItems: 'flex-start', width: '100%' }}
    >
      <div className={adminStyles.adminLayout}>
        <main className={adminStyles.adminContent}>{inner}</main>
      </div>
    </Container>
  );
};

export default AdminPatientSelect;
